import OcrDocumentModal from "../../../../../common/components/modal/ocr-document-modal";
import React, {useRef, useState} from "react";
import axios from "axios";
import Env from "../../../../../util/env";
import Resources from "../../../../../data/services/resources";
import {getJWT} from "../../../../../util/util-auth";
import {pushNotification} from "../../../../../data/actions/ui";
import {useDispatch} from "react-redux";
import {PencilIcon, TrashIcon, XCircleIcon} from "@heroicons/react/24/outline";
import {fieldsToHtml} from "../../../../../common/util/util-fields";
import Card from "../../../../../common/components/card";
import {FieldsManager} from "../../../../../data/services/fields";
import WorkOrdersLaborPartsPrices from "../work-orders-labor-parts-prices";
import {numberWithCommasToBack} from "../../../../../util/util-formaters";
import ResourceTable from "../../../../../common/components/resource-table";
import ModalSaveResource from "../../../../../common/components/modal/modal-save-resource";
import {DEFAULT_IMAGES_ACCEPTABLE_EXTENSIONS} from "../../../../../util/util-constants";

export default function WoLaborPartsOcr({
                                            show,
                                            onClose,
                                            ocrFile,
                                            getFieldsAction,
                                            getPartsFields,
                                            getLaborFields,
                                            getMiscFields,
                                            onSubmit,
                                            selects,
                                            translate,
                                            dataSettings
                                        }) {
    const abortControllerRef = useRef(null);
    const dispatch = useDispatch();

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewData, setPreviewData] = useState(undefined); // Data that will be applied to the form
    const [originalData, setOriginalData] = useState(undefined); // For undo action
    const [items, setItems] = useState([]);
    const [isPartsFromDialogOpen, setIsPartsFormDialogOpen] = useState(false);
    const [isLaborFormDialogOpen, setIsLaborFormDialogOpen] = useState(false);
    const [isMiscFormDialogOpen, setIsMiscFormDialogOpen] = useState(false);

    const [selectedItem, setSelectedItem] = useState(undefined);
    const [isPartsFromDirty, setIsPartsFromDirty] = useState(false);
    const [selectedItemGroupIndex, setSelectedItemGroupIndex] = useState(-1);

    function handleClose() {
        onClose();

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    }

    function handleInputChange(name, value, index, fields) {
        setItems(prevFields => {
            prevFields[index] = FieldsManager.updateField(fields, name, value);
            return [...prevFields];
        })
    }

    function handlePartsInputChange(fields, name, value) {
        fields = FieldsManager.updateField(fields, name, value);
        fields[name].errorMessage = "";
        return fields;
    }

    function handleLaborInputChange(fields, name, value) {
        fields = FieldsManager.updateField(fields, name, value);
        fields[name].errorMessage = "";
        return fields;
    }

    function handleUploadDocument(file) {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const formData = new FormData();
        formData.append('Document', file);


        const isImage = DEFAULT_IMAGES_ACCEPTABLE_EXTENSIONS.includes(file.type);
        const query = isImage ? "?extractor=aws" : "";


        axios.post(
            Env.getDocumentAIApiUrl("api/" + Resources.AiExtractLaborPartsBill + query, {}),
            formData,
            {
                headers: {
                    Authorization: `Bearer ${getJWT().access_token}`,
                },
                signal: controller.signal
            }
        )
            .then((response) => {
                // const fileURL = window.URL.createObjectURL(new Blob([response.data]));
                if (response?.data?.data) {
                    setIsPreviewModalOpen(true);
                    setPreviewData(response?.data?.data); // Data that will be applied to the form
                    setOriginalData(structuredClone(response?.data?.data)); // Undo data
                    setItems((response?.data?.data?.Items ?? []).map(it => {
                        return getFieldsAction(it, false, dataSettings);
                    }));
                } else {
                    dispatch(pushNotification({
                        title: translate('Something went wrong while processing the document.'),
                        messageType: "error",
                        timeout: 16000
                    }));
                }
            })
            .catch((error) => {
                if (axios.isCancel(error)) {
                    console.log('Request was aborted.');
                } else {
                    dispatch(pushNotification({
                        title: translate('Something went wrong while processing the document.'),
                        messageType: "error",
                        timeout: 16000
                    }));

                    console.log("Error processing document:", error)
                    onClose();
                }
            })
    }

    function handleEditPartClick(item, index) {
        setIsPartsFormDialogOpen(true);
        setSelectedItem(item);
        setSelectedItemGroupIndex(index);
    }

    function handleEditLaborClick(item, index) {
        setIsLaborFormDialogOpen(true);
        setSelectedItem(item);
        setSelectedItemGroupIndex(index);
    }

    function handleDeletePartClick(item, index) {
        setPreviewData(prevData => {
            delete prevData?.Items[index].Parts.splice(item.dataIndex, 1);
            return Object.assign({}, prevData);
        })
    }

    function handleDeleteLaborClick(item, index) {
        setPreviewData(prevData => {
            delete prevData?.Items[index].Labor.splice(item.dataIndex, 1);
            return Object.assign({}, prevData);
        })
    }

    function handleDeleteGroupClick(index) {
        setPreviewData(prevData => {
            prevData.Items.splice(index, 1);
            return Object.assign({}, prevData);
        })
    }

    function handleEditMiscClick(item) {
        setIsMiscFormDialogOpen(true);
        setSelectedItem(item);
    }

    function handleDeleteMiscClick(item) {
        setPreviewData(prevData => {
            prevData.Misc.splice(item.dataIndex, 1);
            return Object.assign({}, prevData);
        })
    }

    function handleMiscInputChange(fields, name, value) {
        fields = FieldsManager.updateField(fields, name, value);
        fields[name].errorMessage = "";
        return fields;
    }

    const miscData = (previewData?.Misc ?? []).map(it => {
        it.TotalAmount = numberWithCommasToBack(it.MiscPrice) * (Number(it.MiscQty) ?? 0) * (Number(it.TaxAmount ? it.TaxAmount : 1));
        return it;
    });

    return <OcrDocumentModal
        formModalTitle={translate("text.add_ocr_data")}
        show={show}
        onSubmit={() => onSubmit(previewData, items)}
        submitButtonLabel={translate("text.save")}
        isPreviewModalOpen={isPreviewModalOpen}
        setIsPreviewModalOpen={setIsPreviewModalOpen}
        onFetchFormData={() => {
            setPreviewData(structuredClone(originalData));
        }}
        previewContent={
            <div className="p-6 space-y-4">
                {previewData?.Items.map((it, index) => {
                    let dataPartsTable = it.Parts;
                    dataPartsTable.forEach((element) => {
                        if (!element?.PartQty) {
                            element.PartQty = 1;
                        }
                        element.ind = index
                    });

                    const dataPartsTableCalculate = dataPartsTable.map((it) => {
                        // it.CalculatedPartsPrice = numberWithCommasToBack(it.PartPrice) * Number(it.PartQty) ?? 0;
                        it.CalculatedPartsPrice = numberWithCommasToBack(it.PartPrice) * (Number(it.PartQty) ?? 0) * (Number(it.TaxAmount ? it.TaxAmount : 1));
                        return it;

                    });

                    const dataPartsTableTotal = dataPartsTableCalculate.reduce((memo, it) => {
                        memo += it.CalculatedPartsPrice
                        return memo
                    }, 0)

                    let dataLaborTable = it.Labor;
                    dataLaborTable.forEach((element) => {
                        if (!element?.LaborHours) {
                            element.LaborHours = 1;
                        }
                        element.ind = index
                    });

                    const dataLaborTableCalculate = dataLaborTable.map((it) => {
                        // it.CalculatedLaborPrice = numberWithCommasToBack(it.LaborPrice) * Number(it.LaborHours) ?? 0;
                        it.CalculatedLaborPrice = numberWithCommasToBack(it.LaborPrice) * (Number(it.LaborHours) ?? 0) * (Number(it.TaxAmount ? it.TaxAmount : 1));
                        return it;
                    });

                    const dataLaborTableTotal = dataLaborTableCalculate.reduce((memo, it) => {
                        memo += it.CalculatedLaborPrice
                        return memo
                    }, 0)

                    return <Card key={index}>
                        {/*Action fields*/}
                        <div className={"flex items-center justify-between"}>
                            <h3 className={"text-lg font-bold "}>#{index + 1}</h3>

                            {previewData?.Items.length > 1 && (
                                <button
                                    className={"btn"}
                                    onClick={() => {
                                        handleDeleteGroupClick(index)
                                    }}
                                >
                                    <XCircleIcon className={"w-8 h-8"}/>
                                </button>
                            )}
                        </div>
                        <div className={"grid grid-cols-12 gap-x-6 gap-y-4"} id={`itemIndex${index}`}>
                            {fieldsToHtml(Object.values(Object.assign({}, items[index])), translate, (name, value) => handleInputChange(name, value, index, items[index]), selects)}
                        </div>

                        {/*Parts*/}
                        <div className={"space-y-4"}>
                            <WorkOrdersLaborPartsPrices
                                totalPriceParts={dataPartsTableTotal}
                                totalPriceLabor={dataLaborTableTotal}
                            />
                        </div>

                        <div className="space-y-8">
                            {!!dataPartsTable.length && (
                                <div className="space-y-4">
                                    <div className="text-base font-bold">Parts</div>

                                    <ResourceTable
                                        data={dataPartsTable}
                                        fields={getPartsFields({})}

                                        translate={translate}
                                        options={
                                            {
                                                behaviour: {
                                                    rowSelect: false,
                                                    canAdjustWidth: false,
                                                    hasMenu: false

                                                },
                                                style: {
                                                    condensed: true,
                                                    isGPUAccelerated: true,
                                                    horizontalLines: true,
                                                    verticalLines: true
                                                }
                                            }
                                        }
                                        actions={[
                                            {
                                                title: () => translate('btn.edit'),
                                                action: (item) => handleEditPartClick(item, index),
                                                icon: () => PencilIcon
                                            },
                                            {
                                                title: () => translate('btn.delete'),
                                                action: (item) => handleDeletePartClick(item, index),
                                                icon: () => TrashIcon
                                            }
                                        ]}

                                        onRowClick={handleEditPartClick}
                                    />
                                </div>
                            )}

                            {!!dataLaborTable.length && (
                                <div className="space-y-4">
                                    <div className="text-base font-bold">Labor</div>

                                    <ResourceTable
                                        data={dataLaborTable}
                                        fields={getLaborFields({}, previewData)}

                                        translate={translate}
                                        options={
                                            {
                                                behaviour: {
                                                    rowSelect: false,
                                                    canAdjustWidth: false,
                                                    hasMenu: false

                                                },
                                                style: {
                                                    condensed: true,
                                                    isGPUAccelerated: true,
                                                    horizontalLines: true,
                                                    verticalLines: true
                                                }
                                            }
                                        }

                                        actions={[
                                            {
                                                title: () => translate('btn.edit'),
                                                action: (item) => handleEditLaborClick(item, index),
                                                icon: () => PencilIcon
                                            },
                                            {
                                                title: () => translate('btn.delete'),
                                                action: (item) => handleDeleteLaborClick(item, index),
                                                icon: () => TrashIcon
                                            }
                                        ]}

                                        // onRowClick={handleEditLaborClick}
                                    />
                                </div>
                            )}
                        </div>
                    </Card>
                })}

                {!!miscData.length && (
                    <Card>
                        <div className={"flex justify-center text-lg"}>Misc. charges</div>

                        <ResourceTable
                            data={miscData}
                            fields={getMiscFields({})}

                            translate={translate}
                            options={
                                {
                                    behaviour: {
                                        rowSelect: false,
                                        canAdjustWidth: false,
                                        hasMenu: false
                                    },
                                    style: {
                                        condensed: true,
                                        isGPUAccelerated: true,
                                        horizontalLines: true,
                                        verticalLines: true
                                    }
                                }
                            }
                            actions={[
                                {
                                    title: () => translate('btn.edit'),
                                    action: (item) => handleEditMiscClick(item),
                                    icon: () => PencilIcon
                                },
                                {
                                    title: () => translate('btn.delete'),
                                    action: (item) => handleDeleteMiscClick(item),
                                    icon: () => TrashIcon
                                }
                            ]}

                            onRowClick={handleEditMiscClick}
                        />
                    </Card>
                )}
            </div>
        }
        onClose={handleClose}
        file={ocrFile}
        onUploadDocument={handleUploadDocument}
        translate={translate}
        htmlAfter={
            <>
                <ModalSaveResource
                    show={isPartsFromDialogOpen}
                    initialFocusID="PartName"
                    fields={getPartsFields(selectedItem, null, previewData, () => setIsPartsFormDialogOpen(false), true)}
                    title={selectedItem ? translate("modal_heading.edit_part_item") : translate("modal_heading.add_part")}
                    onClose={() => {
                        setIsPartsFormDialogOpen(false);
                    }}
                    canSubmit={isPartsFromDirty}
                    handleInputChange={handlePartsInputChange}
                    onSubmit={(params) => {
                        if (params?.PartNameLabel) {
                            params.PartName = params.PartNameLabel;
                        }

                        if (!!params) {
                            if (selectedItem) {
                                let previewDataUpdate = Object.assign({}, previewData);

                                previewDataUpdate.Items[selectedItemGroupIndex]['Parts'][selectedItem.dataIndex] = params

                                setIsPartsFormDialogOpen(false);
                                setIsPartsFromDirty(true);
                                setPreviewData(previewDataUpdate);
                            }
                        }
                    }}
                    addFieldContainerClass={"col-span-6"}
                    addBodyClass={"pb-12 text-tm-gray-900"}
                    //resource={resource}
                    metadata={selects}
                    translate={translate}
                />

                <ModalSaveResource
                    show={isLaborFormDialogOpen}
                    initialFocusID="LaborPrice"
                    fields={getLaborFields(selectedItem, previewData)}
                    title={selectedItem?.TotalAmount ? translate("modal_heading.edit_labor_item") : translate("modal_heading.add_labor_item")}
                    onClose={() => {
                        setIsLaborFormDialogOpen(false);
                    }}
                    canSubmit={true}
                    handleInputChange={handleLaborInputChange}
                    onSubmit={(params) => {
                        if (!!params) {
                            let previewDataUpdate = Object.assign({}, previewData);
                            previewDataUpdate.Items[selectedItemGroupIndex]['Labor'][selectedItem.dataIndex] = params;
                            setIsLaborFormDialogOpen(false);
                            setIsPartsFromDirty(true);
                            setPreviewData(previewDataUpdate);
                        }
                    }}
                    addFieldContainerClass={"col-span-6"}
                    addBodyClass={"pb-12 text-tm-gray-900"}
                    metadata={selects}
                    translate={translate}
                />

                <ModalSaveResource
                    show={isMiscFormDialogOpen}
                    fields={getMiscFields(selectedItem)}
                    title={selectedItem ? translate("btn.edit_misc") : translate("btn.add_misc")}
                    onClose={() => {
                        setIsMiscFormDialogOpen(false);
                    }}
                    canSubmit={true}
                    handleInputChange={handleMiscInputChange}
                    onSubmit={(params) => {
                        if (!!params) {
                            let previewDataUpdate = Object.assign({}, previewData);
                            previewDataUpdate.Misc[selectedItem.dataIndex] = params;
                            setIsMiscFormDialogOpen(false);
                            setPreviewData(previewDataUpdate);
                        }
                    }}
                    addFieldContainerClass={"col-span-6"}
                    addBodyClass={"pb-12 text-tm-gray-900"}
                    metadata={selects}
                    translate={translate}
                />
            </>
        }
    />
}